import { CSSProperties, Fragment, JSX, ReactElement } from "react";
import * as CSS from "csstype";

import {
  Hide,
  HStack,
  Show,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Tooltip,
  Tr,
} from "@chakra-ui/react";

import {
  mobileStyleBreakpoint,
  MOBILE_BREAKPOINT_SIZE,
} from "util/styleHelpers";

import SortableTableHeader from "./SortableTableHeader";
import ExpansionRowStat from "./ExpansionRowStat";

export const tablePadding = mobileStyleBreakpoint(3, 12);
export type TablePaddingX = {
  _first?: { paddingLeft: Record<string, number> };
  _last?: { paddingRight: Record<string, number> | number };
};

export const BACK_TO_TOP_MIN_LINES = 10;
export const DOCUMENTS_BACK_TO_TOP_MIN_LINES = 15;

const tableRowDisplay = mobileStyleBreakpoint("flex", "table-row");

const tableCellMaxWidth = mobileStyleBreakpoint<CSS.Property.MaxWidth>(
  "100%",
  "130px"
);

export type ExpansionRow = {
  label?: string;
  mobileSpan?: number;
  mobileAlign?: "flex-start" | "flex-end" | "center";
  value?: JSX.Element | string;
};

const SortableDynamicTable = ({
  colorScheme,
  style,
  variant,
  values,
  columns,
  cellPaddingY = 2,
  tablePaddingX = {
    _first: { paddingLeft: tablePadding },
    _last: { paddingRight: tablePadding },
  },
  hasExpansionRow,
}: {
  colorScheme: string | undefined;
  style?: CSSProperties;
  variant: string;
  values:
    | ContractTerms
    | any
    | (ContractTerms & { expansionRow: ExpansionRow[] }); // update typing here and on line with values.map
  columns: ColumnDefinition<any>[];
  cellPaddingY?: number;
  tablePaddingX?: TablePaddingX;
  hasExpansionRow?: boolean;
}): ReactElement => (
  <Table
    variant={variant}
    colorScheme={colorScheme}
    style={{
      borderCollapse: style?.borderCollapse,
      borderSpacing: style?.borderSpacing,
    }}
  >
    <Hide below={MOBILE_BREAKPOINT_SIZE}>
      <SortableTableHeader
        columns={columns}
        tablePaddingX={tablePaddingX}
        hasExpansionRow={hasExpansionRow}
      />
    </Hide>
    <Tbody>
      {values.map((term: any, index: any) => (
        <Fragment key={index}>
          <Tr
            display={tableRowDisplay}
            flexDirection="column"
            style={{ ...style }}
            _odd={
              variant === "striped"
                ? {
                    td: {
                      background: "grey.50 !important",
                      borderColor: "grey.50 !important",
                    },
                  }
                : undefined
            }
          >
            {columns.map((column) => (
              <Td
                key={`${index}-${column.property}`}
                py={cellPaddingY}
                {...tablePaddingX}
                px={3}
                width={column.width}
                maxWidth={column.maxWidth ?? tableCellMaxWidth}
                borderColor="transparent"
              >
                <HStack w="100%" textOverflow="ellipsis" overflow="hidden">
                  {column.columnLabel && (
                    <Show below={MOBILE_BREAKPOINT_SIZE}>
                      <Text
                        textStyle="body2"
                        color="grey.500"
                        fontWeight="medium"
                        whiteSpace="nowrap"
                        noOfLines={1}
                        overflow={column.overflow}
                      >
                        {column.columnLabel}
                      </Text>
                      <Spacer />
                    </Show>
                  )}
                  {column.hideTooltip ? (
                    <Text
                      textStyle="body2"
                      whiteSpace="nowrap"
                      noOfLines={1}
                      as="span"
                      display="inline"
                      w={
                        column.columnLabel && !column.alwaysFullWidth
                          ? "unset"
                          : "100%"
                      }
                      textAlign={column.alignRight ? "right" : "unset"}
                    >
                      {term[column.property] as string}
                    </Text>
                  ) : (
                    <Tooltip label={term[column.property] as string}>
                      <Text
                        textStyle="body2"
                        whiteSpace="nowrap"
                        noOfLines={1}
                        as="span"
                        display="inline"
                        w={
                          column.columnLabel && !column.alwaysFullWidth
                            ? "unset"
                            : "100%"
                        }
                        textAlign={column.alignRight ? "right" : "unset"}
                      >
                        {term[column.property] as string}
                      </Text>
                    </Tooltip>
                  )}
                </HStack>
              </Td>
            ))}
          </Tr>
          {term.expansionRow && (
            <>
              {term.rowExpanded && (
                <Tr bgColor="grey.50">
                  <Td colSpan={columns.length}>
                    <HStack justify="space-between" alignItems="flex-start">
                      {(term.expansionRow as ExpansionRow[]).map(
                        (column, index) => (
                          <ExpansionRowStat
                            key={column.label ?? `column-${index}`}
                            label={column.label}
                            value={column.value}
                          />
                        )
                      )}
                    </HStack>
                  </Td>
                </Tr>
              )}
              <Tr>
                <Td colSpan={columns.length} p={2} />
              </Tr>
            </>
          )}
        </Fragment>
      ))}
    </Tbody>
  </Table>
);

export default SortableDynamicTable;
